import React, { useRef, useState, useEffect } from "react";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import HC_exporting from "highcharts/modules/exporting";
import HC_export_data from "highcharts/modules/export-data";
import HC_more from "highcharts/highcharts-more";
import styles from './DataExplorerChart.module.scss';
import _ from 'lodash';
import moment from 'moment';

import { Icon } from '../../../../components/Icon';

HC_exporting(Highcharts);
HC_export_data(Highcharts);
HC_more(Highcharts);

// Add Highcharts wrap to modify the 'post' method for exporting
Highcharts.wrap(Highcharts, 'post', function (proceed, url, data, formAttributes) {
    var frame;
    if (!Highcharts.exportFrame) {
        frame = document.createElement('iframe');
        frame.id = 'hc-export-frame';
        frame.style.display = 'none';
        document.body.appendChild(frame);
        Highcharts.exportFrame = frame;
    }
    formAttributes = Highcharts.merge({ target: 'hc-export-frame' }, formAttributes);
    proceed.call(this, url, data, formAttributes);
});

const DataExplorerChart = ({ data, selectedProperties, onRowSelectionChange, cardOpen, loading, timeframeTitle }) => {
    const chartRef = useRef(null);
    const [colors, setColors] = useState({});
    const [primaryAxisDataType, setPrimaryAxisDataType] = useState(null);

    useEffect(() => {
        const newColors = { ...colors };
        const chartSeries = _.get(chartRef, 'current.chart.series', []);
        const usedColors = new Set(chartSeries.map(series => series.color));
        // Slightly adjusted Highcharts colors
        const availableColors = [
            "#7cb5ec",
            "#434348",
            "#90ed7d",
            "#f7a35c",
            "#8085e9",
            "#FF95CA",
            "#e4d354",
            "#2b908f",
            "#f45b5b",
            "#91e8e1"
        ].filter(color => !usedColors.has(color));

        const selectedKeys = new Set(selectedProperties.map(p => `${p.property.property_id}-${p.entity.entity_type_id}`));

        Object.keys(newColors).forEach(key => {
            if (!selectedKeys.has(key)) {
                delete newColors[key];
            }
        });

        selectedProperties.forEach((p) => {
            const key = `${p.property.property_id}-${p.entity.entity_type_id}`;
            if (!newColors[key]) {
                if (availableColors.length > 0) {
                    newColors[key] = availableColors.shift();
                } else {
                    newColors[key] = Highcharts.getOptions().colors[Object.keys(newColors).length % Highcharts.getOptions().colors.length];
                }
            }
        });

        setColors(newColors);
        // eslint-disable-next-line
    }, [selectedProperties, chartRef.current]);

    useEffect(() => {
        // Cleanup function to remove iframe on unmount
        return () => {
            const iframe = document.getElementById('hc-export-frame');
            if (iframe) {
                iframe.parentNode.removeChild(iframe);
            }
        };
    }, []);

    const getYAxes = () => {
        const uniqueDataTypes = [...new Set(data.map(item => item.property.data_type))];

        if (!primaryAxisDataType || !uniqueDataTypes.includes(primaryAxisDataType)) {
            setPrimaryAxisDataType(uniqueDataTypes[0]);
        }

        return uniqueDataTypes.map((dataType, index) => {
            const unit = getUnitFromDataType(dataType).unit;
            const label = getUnitFromDataType(dataType).label;
            const isPrimary = dataType === primaryAxisDataType;

            return {
                title: {
                    text: label,
                },
                opposite: !isPrimary,
                labels: {
                    format: '{value}' + unit,
                }
            };
        });
    }

    const getUnitFromDataType = (dataType) => {
        if (dataType === "tempC") return { label: "Temperature", unit: "°C", decimals: 1 };
        if (dataType === "percent") return { label: "Percent", unit: "%", decimals: 2 };
        if (dataType === "kwh") return { label: "Kilowatt Hours", unit: " kWh", decimals: 2 };
        if (dataType === "kw") return { label: "Kilowatts", unit: " kW", decimals: 2 };
        if (dataType === "degDays") return { label: "Degree Days", unit: "", decimals: 2 };
        if (dataType === "kvarh") return { label: "Reactive Power", unit: " kVArh" };
        if (dataType === "pascals") return { label: "Pressure", unit: " Pa" };
        if (dataType === "ppm") return { label: "CO2", unit: " ppm", decimals: 0 };
        if (dataType === "level") return { label: "Level", unit: "" };
        if (dataType === "decimal") return { label: "Cost", unit: "" };
        if (dataType === "lux") return { label: "Illuminance", unit: ' lx' };
        if (dataType === "m3") return { label: "Volume", unit: ' m3' };
        if (dataType === "integer") return { label: "Count", unit: '' };
        if (dataType === "pf") return { label: "Power Factor", unit: ' PF' };
        if (dataType === "pulse") return { label: "Pulse", unit: '' };
        if (dataType === "ugm3") return { label: "Pollutant Concentration", unit: ' µg/m3' };

        return {
            label: dataType,
            unit: ''
        }
    }

    const getChartSeries = () => {
        return data.map((item) => {
            const seriesData = item.data.map((dataItem) => ({
                x: moment(dataItem.timestamp).valueOf(),
                y: dataItem.value,
            }));

            const label = getUnitFromDataType(item.property.data_type).label;
            const yAxisIndex = getYAxes().findIndex((yAxis) => yAxis.title.text === label);

            const key = `${item.property.property_id}-${item.entity_type.entity_type_id}`;
            const color = colors[key];

            return {
                name: item.property.name,
                building: item.building,
                data: seriesData,
                type: "spline",
                yAxis: yAxisIndex,
                color: color,
                property_id: item.property.property_id,
                entity_type_id: item.entity_type.entity_type_id,
                tooltip: {
                    valueSuffix: getUnitFromDataType(item.property.data_type).unit,
                    valueDecimals: getUnitFromDataType(item.property.data_type).decimals
                }

            };
        });
    }

    const getChartOptions = () => {
        const series = getChartSeries();
        const yAxes = getYAxes();

        return {
            chart: {
                zoomType: "x",
                height: 240
            },
            title: {
                text: timeframeTitle,
                align: 'left',
                style: {
                    fontSize: '14px',
                    fontWeight: 'bold',
                    fontFamily: 'Archivo Semibold, sans-serif',
                }
            },
            legend: {
                enabled: false,
            },
            credits: {
                enabled: false,
            },
            xAxis: {
                gridLineDashStyle: "longdash",
                type: "datetime",
                gridLineWidth: 1,
                gridLineColor: "#e6e6e6",
                minorLineWidth: 1,
                startOnTick: false,
                endOnTick: false,
                labels: {
                    style: {
                        color: "#343a40",
                        fontSize: "9px",
                    },
                },
                plotBands: [],
            },
            yAxis: yAxes,
            tooltip: {
                shared: true,
            },
            plotOptions: {
                series: {
                    turboThreshold: 0,
                    marker: {
                        enabled: false,
                    },
                    groupPadding: 0,
                    pointPadding: 0,
                    borderWidth: 0.2,
                    pointPlacement: "on",
                },
                line: {
                    dataLabels: {
                        enabled: false,
                    },
                    softThreshold: false,
                    series: {
                        marker: {
                            enabled: false,
                        },
                    },
                },
                column: {
                    softThreshold: false,
                },
                area: {
                    stacking: "normal",
                    step: "right",
                    softThreshold: false,
                },
            },
            exporting: {
                enabled: true,
                buttons: {
                    contextButton: {
                        enabled: true,
                        align: 'right',
                        verticalAlign: 'top',
                        symbol: 'menu',
                        x: yAxes.length > 1 ? -80 : -20,
                        menuItems: [
                            "viewFullscreen",
                            "printChart",
                            "downloadPNG",
                            "downloadPDF",
                            "downloadSVG",
                            "downloadCSV",
                            "downloadXLS"]
                    }
                },
            },
            series: series,
        };
    }

    const getChartLabels = () => {
        const chartSeries = _.get(chartRef, 'current.chart.series');

        return <div className={styles.labelWrapper}>
            {selectedProperties.map((p) => {
                const key = `${p.property.property_id}-${p.entity.entity_type_id}`;
                let color = '#eeeeee';
                let bgColor = '#eeeeee33';
                let noData;

                if (chartSeries) {
                    const serie = chartSeries.find((s) => s.userOptions.property_id === p.property.property_id && s.userOptions.entity_type_id === p.entity.entity_type_id);
                    if (serie) {
                        color = colors[key];
                        bgColor = color + '33';
                        noData = false;
                    } else {
                        noData = true;
                    }
                }

                const handleMouseEnter = () => {
                    chartSeries.forEach((s) => {
                        if (s.userOptions.property_id === p.property.property_id && s.userOptions.entity_type_id === p.entity.entity_type_id) {
                            s.setState('hover');
                        } else {
                            s.setState('inactive');
                        }
                    });
                };

                const handleMouseLeave = () => {
                    chartSeries.forEach((s) => {
                        s.setState('normal');
                    });
                };

                if (noData) {
                    return <div key={key} className={styles.label} style={{ background: bgColor, borderColor: color }}
                        onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                        <div className={styles.icon}>
                            {cardOpen && <Icon name='Close' color={'#5e656e'} size={20} onClick={() => onRowSelectionChange(p)} />}
                        </div>
                        <div className={styles.property}>{p.property.name}</div>
                        <div>No Data</div>
                    </div>
                }

                return <div key={key} className={styles.label} style={{ background: bgColor, borderColor: color }}
                    onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                    <div className={styles.icon}>
                        {cardOpen && <Icon name='Close' color={'#5e656e'} size={20} onClick={() => onRowSelectionChange(p)} />}
                    </div>
                    <div className={styles.property}>{p.property.name}</div>
                    <div className={styles.building}>{p.building.name}</div>
                    <div className={styles.entity}>{p.entity.name}</div>
                </div>
            })}
        </div>
    }

    if (selectedProperties.length === 0) {
        return <div style={{
            height: '80px',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
        }}>{cardOpen ? 'Select timeframe and properties to chart' : 'Edit to select timeframe and properties to chart'}</div>
    }

    if (data.length === 0 && !loading) {
        return <div style={{
            height: '100px',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
        }}>No data available for the selected property or timeframe.</div>
    }

    if (selectedProperties.length !== 0 && data.length === 0 && loading) {
        return <div style={{
            height: '240px',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
        }}></div>
    }

    return <div>
        <HighchartsReact highcharts={Highcharts} options={getChartOptions()} ref={chartRef} />
        {getChartLabels()}
    </div>;
};

export default DataExplorerChart;